import "./ClientSubmission.scss";

import { ControlledDateInput } from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { KaboodleToaster } from "components/common";
import Table from "components/Table/Table";
import { format } from "date-fns";
import { omit } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import {
  prescriptionAllocationAPI,
  treatmentAllocationAPI,
  useGetAllPrescriptionAllocationsQuery,
  useGetAllTreatmentAllocationsQuery,
  usePatchPrescriptionAllocationByIdMutation,
  usePatchTreatmentAllocationByIdMutation,
} from "services/msa-api";
import type {
  CostProjectionDataType,
  MSADataType,
  PrescriptionRow,
  TreatmentRow,
} from "types/DataTypes";
import { useToken } from "utils/context";
import { formatCurrency } from "utils/format-utils";

import { getServiceField } from "../future-medical-utils";
import FutureMedicalProfessionalAdminCompany from "./FutureMedicalProfessionalAdminCompany";

import type { AppDispatch } from "services/store";

type AllocationRow = TreatmentRow | PrescriptionRow;
type RowWithType = AllocationRow & { rowType: string };

const isPrescriptionRow = (row: AllocationRow): row is PrescriptionRow => {
  return "ndc" in row;
};

type SummarySectionProps = {
  totals: {
    exposureTotal: number;
    mitigationTotal: number;
    overallTotal: number;
  };
};

const SummarySection = ({ totals }: SummarySectionProps) => {
  const { exposureTotal = 0, mitigationTotal = 0, overallTotal = 0 } = totals;

  return (
    <div className="data-block">
      <div className="data-block-header">Exposures + Mitigations Summary</div>
      <div className="data-values">
        <label>
          <b>Exposure Total</b>
        </label>
        <figure className="w-dollar">{formatCurrency(exposureTotal)}</figure>

        <label>
          <b>Mitigation Total</b>
        </label>
        <figure className="w-dollar">{formatCurrency(mitigationTotal)}</figure>

        <div className="border grid-2-wide" />

        <label>
          <b>Overall Total</b>
        </label>
        <figure className="w-dollar">
          <b>{formatCurrency(overallTotal)}</b>
        </figure>
      </div>
    </div>
  );
};

type AllocationTableProps = {
  rows: RowWithType[];
  title: string;
  onRowChange: (
    newSelectedRows: Set<string | number>,
    currentRow: AllocationRow,
    title: string,
  ) => void;
};

const AllocationTable = ({
  rows,
  title,
  onRowChange,
}: AllocationTableProps) => {
  const renderTotalCell = (row: RowWithType) => {
    let totalValue = 0;

    if (title === "Exposures") {
      totalValue = row.total_exposure ?? 0;
    } else {
      totalValue = row.total_mitigation ?? 0;
    }

    return <div className="text-right">{formatCurrency(totalValue)}</div>;
  };

  const columns = useMemo(
    () => [
      {
        key: "select-row",
        name: "Included",
        width: 70,
        resizable: false,
        renderHeaderCell: () => (
          <div style={{ textAlign: "center", width: "100%" }}>Included</div>
        ),
        renderCell: ({ row }: { row: RowWithType }) => (
          <div
            className={`checkbox-cell ${
              (title === "Exposures" &&
                row.exposure_included_for_client_submission) ||
              (title === "Mitigations" &&
                row.mitigation_included_for_client_submission)
                ? "active"
                : ""
            }`}
            onClick={() => {
              const rowId = `${row.rowType}-${row.id}`;
              onRowChange(new Set([rowId]), row, title);
            }}
          />
        ),
      },
      {
        key: "name",
        name: "Treatments + Prescriptions",
        flex: 1,
      },
      {
        key: `total-${title.toLowerCase()}`,
        name: `Total ${title}`,
        width: 150,
        renderCell: ({ row }: { row: RowWithType }) => renderTotalCell(row),
      },
    ],
    [title],
  );

  const selectedRows = useMemo(
    () =>
      new Set(
        rows
          .filter(
            (row) =>
              row.exposure_included_for_client_submission ||
              row.mitigation_included_for_client_submission,
          )
          .map((row) => `${row.rowType}-${row.id}`),
      ),
    [rows],
  );

  return (
    <div className="data-block">
      <div className="data-block-header">{title}</div>
      <Table
        columns={columns}
        rows={rows}
        rowKeyGetter={(row) => `${row.rowType}-${row.id}`}
        className="exposures-mitigations"
        defaultColumnOptions={{ sortable: false }}
        selectedRows={selectedRows}
        onSelectedRowsChange={(newSelectedRows) => {
          const clickedId = Array.from(newSelectedRows)[0] as string;
          const [rowType, id] = clickedId.split("-");
          const clickedRow = rows.find(
            (row) => row.id === Number(id) && row.rowType === rowType,
          );
          if (!clickedRow) return;
          onRowChange(newSelectedRows, clickedRow, title);
        }}
      />
    </div>
  );
};

type ClientSubmissionFormValues = {
  delivered_to_client_on: string | null;
};

type ClientSubmissionProps = {
  service?: MSADataType | CostProjectionDataType;
  onSave?: (field: string | null, value: unknown) => void;
};

const ClientSubmission = ({
  service: propService,
  onSave,
}: ClientSubmissionProps = {}) => {
  const { accessToken } = useToken();
  const { msaId, costProjectionId } = useParams<{
    msaId?: string;
    costProjectionId?: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();

  const { control, reset } = useForm<ClientSubmissionFormValues>({
    defaultValues: {
      delivered_to_client_on: null,
    },
  });
  const [patchTreatmentAllocation] = usePatchTreatmentAllocationByIdMutation();
  const [patchPrescriptionAllocation] =
    usePatchPrescriptionAllocationByIdMutation();

  const serviceId = msaId || costProjectionId;
  const service =
    propService ||
    ({
      id: Number(serviceId),
      type: msaId ? "Medicare Set-Aside" : "MCP",
    } as MSADataType | CostProjectionDataType);

  const { data: treatmentData, isLoading: isLoadingTreatments } =
    useGetAllTreatmentAllocationsQuery(
      {
        params: getServiceField(service, true),
      },
      { skip: !accessToken || !serviceId },
    );

  const { data: prescriptionData, isLoading: isLoadingPrescriptions } =
    useGetAllPrescriptionAllocationsQuery(
      {
        params: getServiceField(service, true),
      },
      { skip: !accessToken || !serviceId },
    );

  const allTreatments =
    (treatmentData as unknown as { results: TreatmentRow[] })?.results || [];
  const allPrescriptions =
    (prescriptionData as unknown as { results: PrescriptionRow[] })?.results ||
    [];

  useEffect(() => {
    dispatch(setIsLoading(isLoadingTreatments || isLoadingPrescriptions));
  }, [dispatch, isLoadingTreatments, isLoadingPrescriptions]);

  useEffect(() => {
    reset({
      delivered_to_client_on: service.delivered_to_client_on || null,
    });
  }, [service, reset]);

  const { sortedExposures, sortedMitigations } = useMemo(() => {
    const mapWithType = (rows: AllocationRow[], type: string) =>
      rows.map((row) => ({ ...row, rowType: type }));

    const allRows = [
      ...mapWithType(allTreatments, "treatment"),
      ...mapWithType(allPrescriptions, "prescription"),
    ];

    // only include rows that are either exposure or mitigation and are review_type is LEGAL
    const includedRows = allRows.filter(
      (row) =>
        row.review_type === "LEGAL" && (row.has_exposure || row.has_mitigation),
    );

    return {
      sortedExposures: includedRows
        .filter((row) => row.has_exposure)
        .sort((a, b) => a.name.localeCompare(b.name)),
      sortedMitigations: includedRows
        .filter((row) => row.has_mitigation)
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [allTreatments, allPrescriptions]);

  const totals = useMemo(() => {
    return {
      exposureTotal:
        service.total_exposure_and_mitigation_client_delivery?.exposure_total ??
        0,
      mitigationTotal:
        service.total_exposure_and_mitigation_client_delivery
          ?.mitigation_total ?? 0,
      overallTotal:
        service.total_exposure_and_mitigation_client_delivery
          ?.exposure_mitigations_total ?? 0,
    };
  }, [service]);

  const handleSave = useCallback(
    (field: string) => async (value: unknown) => {
      if (onSave) {
        try {
          let formattedValue = value;
          if (field === "delivered_to_client_on" && value instanceof Date) {
            formattedValue = format(value, "yyyy-MM-dd");
          }
          await onSave(field, formattedValue);
        } catch (error) {
          KaboodleToaster.error("Failed to update delivery date");
          console.error("Failed to update delivery date:", error);
        }
      }
    },
    [onSave],
  );

  if (!serviceId || !accessToken) return null;

  const handleSelectedRowsChange = async (
    _newSelectedRows: Set<string | number>,
    currentRow: AllocationRow,
    title: string,
  ) => {
    const newExposureValue =
      title === "Exposures"
        ? !currentRow.exposure_included_for_client_submission
        : currentRow.exposure_included_for_client_submission;
    const newMitigationValue =
      title === "Mitigations"
        ? !currentRow.mitigation_included_for_client_submission
        : currentRow.mitigation_included_for_client_submission;

    let patchResult: { undo: () => void };

    if (isPrescriptionRow(currentRow)) {
      patchResult = dispatch(
        prescriptionAllocationAPI.util.updateQueryData(
          "getAllPrescriptionAllocations",
          {
            params: getServiceField(service, true),
          },
          (draft) => {
            const row = (
              draft as unknown as { results: PrescriptionRow[] }
            ).results.find((r: PrescriptionRow) => r.id === currentRow.id);
            if (!row) return;
            if (row.has_exposure) {
              row.exposure_included_for_client_submission = newExposureValue;
            }
            if (row.has_mitigation) {
              row.mitigation_included_for_client_submission =
                newMitigationValue;
            }
          },
        ),
      );
    } else {
      patchResult = dispatch(
        treatmentAllocationAPI.util.updateQueryData(
          "getAllTreatmentAllocations",
          {
            params: getServiceField(service, true),
          },
          (draft) => {
            const row = (
              draft as unknown as { results: TreatmentRow[] }
            ).results.find((r: TreatmentRow) => r.id === currentRow.id);
            if (!row) return;
            if (row.has_exposure) {
              row.exposure_included_for_client_submission = newExposureValue;
            }
            if (row.has_mitigation) {
              row.mitigation_included_for_client_submission =
                newMitigationValue;
            }
          },
        ),
      );
    }

    try {
      const basePatchData = {
        exposure_included_for_client_submission: newExposureValue,
        mitigation_included_for_client_submission: newMitigationValue,
        name: currentRow.name,
        exposure_detail_type: currentRow.exposure_detail_type,
        number_of_years: currentRow.number_of_years,
        number_of_years_exposed: currentRow.number_of_years_exposed,
        number_of_years_mitigated: currentRow.number_of_years_mitigated,
        exposed_unit_price: currentRow.exposed_unit_price,
        mitigated_unit_price: currentRow.mitigated_unit_price,
        ...getServiceField(service, false),
      };

      const patchData = isPrescriptionRow(currentRow)
        ? {
            ...basePatchData,
            ndc: currentRow.ndc,
            dosage: currentRow.dosage,
            units_per_month: currentRow.units_per_month,
            units_per_year: currentRow.units_per_year,
            unit_price: currentRow.unit_price,
            exposed_units_per_month: currentRow.exposed_units_per_month,
            mitigated_units_per_month: currentRow.mitigated_units_per_month,
            exposed_units_per_year: currentRow.exposed_units_per_year,
            mitigated_units_per_year: currentRow.mitigated_units_per_year,
          }
        : {
            ...basePatchData,
            units_per_year: currentRow.units_per_year,
            exposed_units_per_year: currentRow.exposed_units_per_year,
            mitigated_units_per_year: currentRow.mitigated_units_per_year,
          };

      const fieldsToOmit = [
        "cost_projection",
        "total_cost",
        "annual_cost",
        "organization",
        "modified",
        "created",
        "version",
        "id",
        "msa",
        "cost_projection",
      ];

      const finalPatchData = omit(patchData, fieldsToOmit);

      if (isPrescriptionRow(currentRow)) {
        await patchPrescriptionAllocation({
          id: currentRow.id,
          data: finalPatchData,
        }).unwrap();
      } else {
        await patchTreatmentAllocation({
          id: currentRow.id,
          data: finalPatchData,
        }).unwrap();
      }
    } catch (err) {
      patchResult.undo();
      KaboodleToaster.error("Failed to update selection status");
      console.error("Failed to update selection status:", err);
    }
  };

  return (
    <div className="swim-column">
      <FutureMedicalCard cardNumber={1} heading="Client Delivery Information">
        <ControlledDateInput
          control={control}
          name="delivered_to_client_on"
          label="Delivered to Client On"
          errors={{}}
          onSave={handleSave("delivered_to_client_on")}
          allowFutureDates={true}
          isClearable={true}
        />
      </FutureMedicalCard>
      <FutureMedicalProfessionalAdminCompany
        service={service}
        onSave={onSave}
      />
      <AllocationTable
        rows={sortedExposures}
        title="Exposures"
        onRowChange={(newSelectedRows, currentRow) =>
          handleSelectedRowsChange(newSelectedRows, currentRow, "Exposures")
        }
      />
      <AllocationTable
        rows={sortedMitigations}
        title="Mitigations"
        onRowChange={(newSelectedRows, currentRow) =>
          handleSelectedRowsChange(newSelectedRows, currentRow, "Mitigations")
        }
      />
      <SummarySection totals={totals} />
    </div>
  );
};

export default ClientSubmission;
